import { useId } from "react";

import { SpinnerIcon } from "@/assets/icons";
import { IOptionItem } from "@/components/ui/dropdown/types";
import InfoNote from "@/components/ui/InfoNote";
import RadioButton from "@/components/ui/radio";

import { twMerge } from "tailwind-merge";

interface AttributeSelectionProps {
  label?: string;
  description?: string;
  items: IOptionItem[];
  isLoading?: boolean;
  selectedValue: string;
  onSelect: (value: string) => void;
  /** Shown in place of the list once it loads empty. Without it, an empty list
   * stays an empty box. */
  emptyMessage?: string;
}

const AttributeSelection = ({
  label,
  description,
  items,
  selectedValue,
  isLoading = false,
  onSelect,
  emptyMessage
}: AttributeSelectionProps) => {
  // One group per list, so a second list on screen keeps its own selection.
  const groupName = useId();
  const isEmptyNoteShown = Boolean(
    emptyMessage && !isLoading && items.length === 0
  );

  return (
    <div className="flex flex-col gap-3 mt-4">
      {label && (
        <p className="text-sm text-txtHeading font-semibold">{label}</p>
      )}
      {description && <span className="text-xs">{description}</span>}
      {isEmptyNoteShown ? (
        <InfoNote
          variant="muted"
          message={emptyMessage}
          data-testid="attribute-selection-empty-note"
        />
      ) : (
        <div className={twMerge("rounded-xl border border-gainsboro p-3")}>
          <div className="flex flex-col gap-3 max-h-[200px] overflow-y-auto custom-scrollbar">
            {isLoading ? (
              <div className="flex flex-1 items-center justify-center p-3">
                <SpinnerIcon
                  data-testid="attribute-selection-spinner"
                  className="w-6 h-6 animate-spin text-light-gray"
                />
              </div>
            ) : (
              items.map((item, index) => (
                <RadioButton
                  key={`${item.value}-${index}`}
                  name={groupName}
                  id={`${groupName}-${index}`}
                  checked={selectedValue === item.value}
                  onCheck={() => onSelect(item.value)}
                  label={item.label}
                  inputClassName="w-5 h-5 border-dim-gray transition-none"
                  labelClassName="text-xs font-normal text-txtHeading"
                />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AttributeSelection;
